import React from 'react';
import { useTranslation } from 'react-i18next';
import Style from './seat.module.css';

const ORIGIN_MAPPING = {
    weez_web: 'online_sale',
    weez_ticketoffice: 'ticket_office',
    weez_invitation: 'invitation',
    weez_manual_add: 'manual_add',
    partner: 'partner'
};

const SEAT_INFO_FIELDS = {
    full_name: {
        labelKey: 'ticket.availability.hover_seat_infos.full_name',
        value: attendee => (attendee?.first_name && attendee?.last_name ? `${attendee.first_name} ${attendee.last_name}` : ''),
        showInModes: ['hover', 'detail'],
        showForStates: [],
        condition: attendee => !!attendee
    },
    origin: {
        labelKey: 'ticket.availability.hover_seat_infos.origin',
        value: (attendee, activeBooking, t) => {
            let originKey = '';
            let originName = '';
            if (activeBooking?.batch_booking) {
                originKey = 'partner';
                originName = activeBooking.batch_booking.partner?.name || '';
            } else if (attendee?.order) {
                originKey = ORIGIN_MAPPING[attendee.order.origin] || attendee.order.origin;
                originName = attendee.order.origin_detail;
            }
            const label = t(`ticket.availability.hover_seat_infos.origins.${originKey}`);
            return originName ? `${label} (${originName})` : label;
        },
        showInModes: ['hover', 'detail'],
        showForStates: ['BOOKED', 'RESERVED', 'BOOKING_IN_PROGRESS', 'PARTIALLY_BOOKED', 'PARTIALLY_AVAILABLE', 'BOOKED_BY_RESERVATION'],
        condition: (attendee, activeBooking) => !!attendee || !!activeBooking?.batch_booking || !!activeBooking?.reservation
    },
    rate_name: {
        labelKey: 'ticket.availability.hover_seat_infos.rate',
        value: (_, activeBooking) => activeBooking?.rate_name || '',
        showInModes: ['detail'],
        showForStates: ['BOOKED', 'RESERVED'],
        condition: attendee => !!attendee
    },
    buyer_name: {
        labelKey: 'ticket.availability.hover_seat_infos.buyer_name',
        value: attendee => (attendee?.order ? `${attendee.order.buyer_first_name} ${attendee.order.buyer_last_name}` : ''),
        showInModes: ['detail'],
        showForStates: ['BOOKED', 'RESERVED'],
        condition: attendee => !!attendee
    },
    inventory: {
        labelKey: 'ticket.availability.hover_seat_infos.inventory',
        value: (_, activeBooking) => activeBooking?.inventory_name || '',
        showInModes: ['detail'],
        showForStates: ['BOOKED', 'RESERVED', 'PARTIALLY_BOOKED', 'PARTIALLY_AVAILABLE', 'BOOKING_IN_PROGRESS'],
        condition: (attendee, activeBooking) => !!attendee || !!activeBooking
    },
    barcode: {
        labelKey: 'ticket.availability.hover_seat_infos.barcode',
        value: attendee => attendee?.barcode || '',
        showInModes: ['detail'],
        showForStates: ['BOOKED', 'PARTIALLY_BOOKED', 'PARTIALLY_AVAILABLE'],
        condition: attendee => !!attendee
    },
    order_id: {
        labelKey: 'ticket.availability.hover_seat_infos.order_id',
        value: attendee => attendee?.order?.transaction_reference || '',
        showInModes: ['detail'],
        showForStates: ['BOOKED', 'PARTIALLY_BOOKED', 'PARTIALLY_AVAILABLE'],
        condition: attendee => !!attendee
    },
    batch_name: {
        labelKey: 'ticket.availability.hover_seat_infos.reservation',
        value: (attendee, activeBooking) => activeBooking?.batch_booking?.name || activeBooking?.reservation?.name || '',
        showInModes: ['hover'],
        showForStates: ['RESERVED', 'BOOKING_IN_PROGRESS', 'BOOKED_BY_RESERVATION'],
        condition: activeBooking => !!activeBooking?.batch_booking || !!activeBooking?.reservation
    }
};

export default function SeatInfoBlocks({ activeBooking, bookingState, mode = 'detail' }) {
    const { t } = useTranslation();

    if (!activeBooking && mode === 'detail') {
        return null;
    }

    const attendee = activeBooking?.attendee;

    const visibleFields = Object.values(SEAT_INFO_FIELDS).filter(field => {
        if (!field.showInModes.includes(mode)) return false;
        if (field.showForStates.length > 0 && !field.showForStates.includes(bookingState?.toUpperCase())) return false;
        return field.condition(attendee, activeBooking);
    });

    if (visibleFields.length === 0 && mode === 'hover') {
        return null;
    }

    return (
        <div className={Style['booking-info']}>
            {visibleFields.length > 0 && (
                <div className={Style['info-block']}>
                    {visibleFields.map(field => {
                        const fieldValue = field.value(attendee, activeBooking, t);
                        if (!fieldValue) return null;

                        return (
                            <div key={field.labelKey} className={Style['info-block-item']}>
                                <span className={Style['info-block-label']}>{t(field.labelKey)}</span>
                                <span className={Style['info-block-value']}>{fieldValue}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
